// src/Components/Shared/MediaSlide.js

import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AudioSlide from './AudioSlide';

/**
 * A smart wrapper that renders Image, Video, or Audio.
 * Includes strict guards against stale "null" URLs causing endless buffering.
 */
const MediaSlide = ({
  item,
  isActive,
  objectFit = 'contain',
  shouldAutoPlay = false,
  isGlobalMuted = true,
  sx = {},
}) => {
  // 1. ALL HOOKS MUST BE DECLARED FIRST
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [imgError, setImgError] = useState(false);

  // AMENDED: Expansive extraction net to ensure all potential URL variants are caught
  const mediaSrc =
    item.url ||
    item.img ||
    item.videoUrl ||
    item.video ||
    item.src ||
    item.media ||
    item.clipUrl ||
    item.downloadUrl ||
    '';

  const isAudio =
    item.type === 'audio' ||
    (typeof item.title === 'string' && item.title.match(/\.(mp3|wav|ogg|aac)/i)) ||
    (typeof mediaSrc === 'string' && mediaSrc.match(/\.(mp3|wav|ogg|aac)/i));

  // AMENDED: Greatly expanded regex extension list to guarantee video clip matching
  const isVideo =
    !isAudio &&
    (item.type === 'video' ||
      (typeof item.title === 'string' &&
        item.title.match(/\.(mp4|mov|webm|avi|mkv|wmv|flv|mts|m4v)/i)) ||
      (typeof mediaSrc === 'string' &&
        mediaSrc.match(/\.(mp4|mov|webm|avi|mkv|wmv|flv|mts|m4v)/i)));

  useEffect(() => {
    setImgError(false);
  }, [mediaSrc]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = () => {};
    container.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [isVideo]);

  useEffect(() => {
    let isMounted = true;
    const video = videoRef.current;

    if (!video) return;

    const attemptPlay = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 50));

        if (isMounted && isActive && shouldAutoPlay) {
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.info('Playback interrupted');
        }
      }
    };

    if (isActive) {
      if (shouldAutoPlay) {
        attemptPlay();
      }
    } else {
      video.pause();
    }

    return () => {
      isMounted = false;
      if (video) video.pause();
    };
  }, [isActive, shouldAutoPlay]);

  // 2. EARLY RETURNS / RENDERS ONLY HAPPEN AFTER ALL HOOKS

  if (!mediaSrc || mediaSrc === 'null' || mediaSrc === 'undefined') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#63c2e8',
          ...sx,
        }}>
        <Typography
          variant='h6'
          sx={{ color: '#fff', fontStyle: 'italic', fontWeight: 'normal' }}>
          No Media Available
        </Typography>
      </Box>
    );
  }

  if (isAudio) {
    return (
      <AudioSlide
        item={item}
        isActive={isActive}
        shouldAutoPlay={shouldAutoPlay}
        isGlobalMuted={isGlobalMuted}
        sx={sx}
      />
    );
  }

  if (isVideo) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
          position: 'relative',
          ...sx,
        }}>
        <video
          ref={videoRef}
          src={mediaSrc}
          controls={!shouldAutoPlay}
          muted={isGlobalMuted}
          playsInline
          preload='metadata'
          onLoadedMetadata={(e) => {
            if (!shouldAutoPlay) {
              e.target.currentTime = 0.1;
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: objectFit,
          }}
        />
      </Box>
    );
  }

  if (imgError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#63c2e8',
          ...sx,
        }}>
        <Typography
          variant='h6'
          sx={{ color: '#000', fontStyle: 'italic', fontWeight: 'normal' }}>
          Image File Not Found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component='img'
      src={mediaSrc}
      alt={item.markerName || 'Media'}
      onError={() => setImgError(true)}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: objectFit,
        objectPosition: 'center',
        display: 'block',
        ...sx,
      }}
    />
  );
};

export default MediaSlide;
